import {
  SortDescriptor,
  GroupDescriptor,
  CompositeFilterDescriptor,
} from '@progress/kendo-data-query';

/**
 * Options for configuring a Kendo data source.
 * 
 * Includes filtering, grouping, and sorting configurations used to
 * define the state of a data source for grid persistence.
 * @internal
 */
interface DataSourceOptions {
  /** Filter descriptor for data source filtering. */
  filter?: CompositeFilterDescriptor;
  /** Array of group descriptors for data source grouping. */
  group?: GroupDescriptor[];
  /** Array of sort descriptors for data source sorting. */
  sort?: SortDescriptor[];
}

/**
 * Configuration for a single grid column.
 * 
 * Includes display properties, sizing, and ordering information
 * necessary to restore column state when loading grid configuration.
 * @internal
 */
interface GridColumn {
  /** Unique identifier for the column. */
  key: string;
  /** Field name that the column is bound to in the data source. */
  field?: string;
  /** Whether the column is currently hidden from view. */
  hidden: boolean;
  /** Display title for the column header. */
  title?: string;
  /** Width of the column in pixels. */
  width: number;
  /** Ordering index for column positioning. */
  orderIndex: number;
}

/**
 * Base interface for local storage data.
 * 
 * Provides common properties for data type identification and versioning
 * to ensure compatibility when persisting and retrieving data from localStorage.
 * @internal
 */
interface LocalStorageData {
  /** Identifier for the type of data stored. */
  DataType: string;
  /** Version identifier for the stored data format. */
  Version: string;
}

/**
 * Complete state configuration for a Kendo Grid.
 * Extends LocalStorageData to provide persistence capabilities for grid state.
 * 
 * This interface represents all the information needed to restore a grid's
 * visual and functional state, including column configuration, sorting,
 * filtering, and grouping settings.
 * 
 * @example
 * ```typescript
 * const gridState: KendoGridState = {
 *   DataType: 'GridState',
 *   Version: '1.0',
 *   dataSourceOptions: {
 *     sort: [{ field: 'name', dir: 'asc' }]
 *   },
 *   columns: [
 *     { key: 'name', field: 'name', hidden: false, width: 200, orderIndex: 0 }
 *   ],
 *   tableWidth: 800
 * };
 * ```
 */
export interface KendoGridState extends LocalStorageData {
  /** Data source configuration including filters, sorting, and grouping. */
  dataSourceOptions: DataSourceOptions;
  /** Array of column configurations for the grid. */
  columns: GridColumn[];
  /** Total width of the grid table in pixels. */
  tableWidth?: number;
}

/**
 * Prefix used for localStorage keys when storing grid state.
 * 
 * Combined with grid ID to create unique storage keys for persisting
 * grid state data in the browser's localStorage.
 */
export const LocalStorageKeyPrefix = 'GridState';

/**
 * Configuration options for grid state management.
 * Controls how grid state is loaded, saved, and applied.
 * 
 * @example
 * ```typescript
 * const config: GridStateConfig = {
 *   gridId: 'user-grid',
 *   version: '1.0',
 *   loadState: true,
 *   absoluteColumnSizes: false
 * };
 * ```
 */
export interface GridStateConfig {
  /** Unique identifier for the grid, used in localStorage key generation. */
  gridId: string;
  /** Version string for the grid state format, used for compatibility checking. */
  version: string;
  /** Whether to automatically load saved state when the grid initializes. */
  loadState: boolean;
  /** 
   * Whether to use absolute pixel sizes for columns instead of relative sizing.
   * @default false
   */
  absoluteColumnSizes?: boolean;
}